'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import {
  type ProjectReferenceAspect,
  PROJECT_REFERENCE_ASPECTS,
} from '@/types/project-reference';

const ASPECT_COLORS: Record<ProjectReferenceAspect, string> = {
  style: 'bg-blue-100 text-black dark:bg-blue-900/30 dark:text-white',
  material: 'bg-amber-100 text-black dark:bg-amber-900/30 dark:text-white',
  color: 'bg-pink-100 text-black dark:bg-pink-900/30 dark:text-white',
};

interface AspectsPillsProps {
  // Multi-select set. Order is irrelevant — the server stores canonically.
  selected: ProjectReferenceAspect[];
  onChange: (aspects: ProjectReferenceAspect[]) => void;
  disabled?: boolean;
}

/**
 * Multi-toggle aspect chips (style / material / color). Replaces the
 * single-select CategoryPills: a reference can carry any combination of aspects.
 * Toggling never empties the set — the last selected chip can't be removed, so a
 * reference always keeps at least one aspect (mirrors the server's never-empty
 * guarantee, which would otherwise hide it from every "All" render bucket).
 */
export function AspectsPills({
  selected,
  onChange,
  disabled,
}: AspectsPillsProps) {
  const t = useTranslations('projectReference');

  function toggle(aspect: ProjectReferenceAspect) {
    if (selected.includes(aspect)) {
      // Don't allow removing the last aspect.
      if (selected.length === 1) return;
      onChange(selected.filter((a) => a !== aspect));
    } else {
      onChange([...selected, aspect]);
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {PROJECT_REFERENCE_ASPECTS.map((aspect) => {
        const isSelected = selected.includes(aspect);
        return (
          <button
            key={aspect}
            type="button"
            disabled={disabled}
            aria-pressed={isSelected}
            onClick={() => toggle(aspect)}
            className={cn(
              'px-3 py-1.5 rounded-full text-chip whitespace-nowrap transition-colors',
              isSelected
                ? ASPECT_COLORS[aspect]
                : 'bg-muted text-muted-foreground'
            )}
          >
            {t(`aspect.${aspect}`)}
          </button>
        );
      })}
    </div>
  );
}
